import React, { useEffect, useContext, useState, useMemo } from 'react';
import { useAppController } from 'context';
import { ChartContext } from './ChartProvider';
import {
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    Legend,
    Brush,
    ResponsiveContainer,
    ReferenceLine,
    ReferenceArea,
    AreaChart,
    Area,
} from 'recharts';
import {
    getChartColor,
    calculateBrushIndexes,
    formatYAxis,
    getYaxisDomain,
    simpleDateFormat,
    tickFormatter,
    getPowerDomain,
} from '../DeviceDetails/helper';
import './index.css';
import {
    ChartLabels,
    ShortUnits,
    StatKeys,
    TabValues,
} from '../DeviceDetails/constants';
import ChartGradients from './ChartGradients';
import { removeSpaces } from './helper';
import CustomTooltip from './CustomTooltip'; // Import the custom tooltip
import CustomLegend from './CustomLegend';
import { Skeleton } from 'components/shadcn/ui/skeleton';

const ChartMain = () => {
    const [{ darkMode }] = useAppController();
    const {
        stats,
        statsLoading,
        chartType,
        viewMode,
        lineLabels,
        brushStartIndex,
        setBrushStartIndex,
        brushEndIndex,
        setBrushEndIndex,
        key,
        setKey,
        showLines,
        setShowResetZoom,
        missingRanges,
    } = useContext(ChartContext);

    const yAxisDomain = useMemo(() => {
        return getYaxisDomain(stats, chartType, lineLabels);
    }, [stats, chartType, lineLabels]);

    useEffect(() => {
        const { startIndex, endIndex } = calculateBrushIndexes(stats, viewMode);
        setBrushStartIndex(startIndex);
        setBrushEndIndex(endIndex);
        setShowResetZoom(false);
    }, [
        stats,
        viewMode,
        setShowResetZoom,
        setBrushStartIndex,
        setBrushEndIndex,
    ]);

    const handleSliderChange = ({ startIndex, endIndex }) => {
        setBrushStartIndex(startIndex);
        setBrushEndIndex(endIndex);
        setShowResetZoom(true);
    };

    // Need to use a key but won't need it for brush indexes after
    // https://github.com/recharts/recharts/issues/2404 is in the fix version which we pull
    // https://github.com/ckifer has a fix for this but we'd need to update the library to
    const [refAreaLeft, setRefAreaLeft] = useState('');
    const [refAreaRight, setRefAreaRight] = useState('');

    const zoom = () => {
        setKey(key + 1);
        setShowResetZoom(true);
        if (refAreaLeft === refAreaRight || refAreaRight === '') {
            setRefAreaLeft('');
            setRefAreaRight('');
            return;
        }
        // Find indexes of refAreaLeft and refAreaRight in the stats array
        const startIndex = stats.findIndex(
            (stat) => stat.isodate_utc === refAreaLeft
        );
        const endIndex = stats.findIndex(
            (stat) => stat.isodate_utc === refAreaRight
        );
        // Set brush indexes
        setBrushStartIndex(Math.min(startIndex, endIndex));
        setBrushEndIndex(Math.max(startIndex, endIndex));
        // Reset refAreaLeft and refAreaRight
        setRefAreaLeft('');
        setRefAreaRight('');
    };

    const showLinesLeft =
        chartType.title === TabValues.All
            ? showLines.filter((label) => label !== ChartLabels.Power)
            : showLines;

    const showLinesRight = showLines.filter(
        (label) => label === ChartLabels.Power
    );

    const hideFill = showLinesLeft.length > 2;

    const leftLines = showLinesLeft?.map((label, i) => {
        return (
            <Area
                yAxisId={'left'}
                key={label}
                type="monotone"
                dot={false}
                dataKey={label}
                stroke={getChartColor(label)}
                name={`${label}`}
                fill={
                    hideFill
                        ? 'none'
                        : `url(#chartGradient-${removeSpaces(label)})`
                } // Add gradient fill for single stat
                fillOpacity={0.5} // Adjust the opacity as needed
            />
        );
    });

    const rightLines = showLinesRight.map((label) => {
        return (
            <Area
                yAxisId={'right'}
                key={label}
                type="monotone"
                dot={false}
                dataKey={label}
                stroke={getChartColor(label)}
                name={`${label}`}
                fill={`url(#chartGradient-${removeSpaces(label)})`} // Add gradient fill here
                fillOpacity={0.5} // Adjust the opacity as needed
            />
        );
    });

    const powerDomain = useMemo(() => {
        return getPowerDomain(stats);
    }, [stats]);

    // LOADING
    if (statsLoading) {
        return (
            <div className="mt-8">
                <Skeleton className="h-[350px] w-full" />
            </div>
        );
    }

    // NO DATA DISPLAY
    if (stats?.length === 0) {
        return (
            <div className="flex h-32 items-center justify-center">
                <h2 className="self-center text-4xl font-bold opacity-20">
                    {ChartLabels.NoData}
                </h2>
            </div>
        );
    }

    //CHART
    return (
        <div className="mt-8">
            <ResponsiveContainer width="100%" height={350}>
                <AreaChart
                    key={key}
                    height={350}
                    data={stats ? stats : []}
                    margin={{
                        top: 20, // Increased from 0 to 20
                        right: 0,
                        left: 0,
                        bottom: 0,
                    }}
                    onMouseDown={(e) => setRefAreaLeft(e?.activeLabel)}
                    onMouseMove={(e) =>
                        refAreaLeft && setRefAreaRight(e?.activeLabel)
                    }
                    onMouseUp={zoom}
                >
                    <Legend
                        verticalAlign="top"
                        align="center"
                        iconType="plainline"
                        height={60}
                        wrapperStyle={{
                            top: 0,
                            padding: '0 20px',
                        }}
                        content={<CustomLegend chartType={chartType} />}
                    />
                    <defs>
                        {/* defs are scoped to React component, so don't move it into ChartGradients */}
                        <ChartGradients lines={showLines} />
                        <pattern
                            id="missingHatch"
                            patternUnits="userSpaceOnUse"
                            width="8"
                            height="8"
                            patternTransform="rotate(45)"
                        >
                            <rect
                                width="8"
                                height="8"
                                fill={darkMode ? '#334155' : '#e5e7eb'}
                                fillOpacity={0.5}
                            />
                            <line
                                x1="0"
                                y1="0"
                                x2="0"
                                y2="8"
                                stroke={darkMode ? '#64748b' : '#94a3b8'}
                                strokeWidth="2"
                            />
                        </pattern>
                    </defs>

                    <XAxis
                        dataKey={StatKeys.DATE_UTC}
                        tickFormatter={(date) => tickFormatter(date, viewMode)}
                        padding={'gap'}
                        tickMargin={5}
                        tickCount={5}
                        minTickGap={25}
                        fontSize={16}
                        height={50}
                    />
                    {leftLines?.length > 0 && (
                        <YAxis
                            unit={
                                chartType.title !== TabValues.All
                                    ? ShortUnits[showLines[0]]
                                    : ''
                            }
                            yAxisId={'left'}
                            domain={yAxisDomain}
                            fontSize={12}
                            tickSize={10}
                            tickFormatter={formatYAxis}
                        />
                    )}
                    {chartType.title === TabValues.All &&
                        rightLines?.length > 0 && (
                            <YAxis
                                unit={'W'}
                                yAxisId={'right'}
                                orientation="right"
                                domain={powerDomain}
                                fontSize={12}
                                tickSize={10}
                                tick={{
                                    stroke: getChartColor(ChartLabels.Power),
                                    strokeWidth: 0.5,
                                }}
                                tickFormatter={formatYAxis}
                            />
                        )}

                    {/* Replace the default Tooltip with CustomTooltip */}
                    <Tooltip
                        content={<CustomTooltip />}
                        cursor={{
                            stroke: 'red',
                            strokeWidth: 2,
                        }}
                        position={{ y: 160 }}
                    />

                    <CartesianGrid stroke={darkMode ? '#334155' : '#e5e7eb'} strokeDasharray="5 5" />

                    {leftLines?.length > 0 && (
                        <ReferenceLine
                            y={0}
                            scale={'linear'}
                            yAxisId={'left'}
                            stroke="black"
                            strokeDasharray="3 3"
                        />
                    )}
                    {leftLines?.length > 0 && missingRanges?.map(({ x1, x2 }) => (
                        <ReferenceArea
                            key={`missing-${x1}`}
                            x1={x1}
                            x2={x2}
                            yAxisId={'left'}
                            fill="url(#missingHatch)"
                            strokeOpacity={0}
                        />
                    ))}
                    {/* Invisible anchor series so the tooltip fires over missing-day buckets where every real series is null. */}
                    <Area
                        yAxisId={'left'}
                        type="monotone"
                        dataKey="__tooltipAnchor"
                        stroke="none"
                        fill="none"
                        dot={false}
                        activeDot={false}
                        legendType="none"
                        name="__tooltipAnchor"
                        isAnimationActive={false}
                    />
                    {leftLines}
                    {chartType.title === TabValues.All && rightLines}
                    <Brush
                        dataKey={StatKeys.DATE_UTC}
                        height={14}
                        stroke=""
                        startIndex={brushStartIndex}
                        endIndex={brushEndIndex}
                        tickFormatter={simpleDateFormat}
                        fill={darkMode ? '#1e293b' : '#C4CDE0'}
                        gap={1}
                        travellerWidth={14}
                        onChange={handleSliderChange}
                    />
                    {refAreaLeft && refAreaRight ? (
                        <ReferenceArea
                            x1={refAreaLeft}
                            x2={refAreaRight}
                            yAxisId={'left'}
                            strokeOpacity={0.3}
                        />
                    ) : null}
                </AreaChart>
            </ResponsiveContainer>
        </div>
    );
};

export default ChartMain;
